<template>
  <v-container>
    <h2 class="text-h4 mb-6">Autoimport</h2>

    <v-sheet class="mb-6">
      <v-row>
        <v-col cols="12" md="4" lg="3">
          <v-card outlined class="pa-4">
            <div class="text-subtitle-1 font-weight-medium mb-3">Valitse päivä</div>
            <v-date-picker
              v-model="selectedDate"
              :max="today"
              @input="handleDateChange"
              full-width
              no-title
            ></v-date-picker>
          </v-card>
        </v-col>

        <v-col cols="12" md="8" lg="9">
          <v-card outlined class="pa-4 h-100">
            <div class="text-subtitle-1 font-weight-medium mb-3">Hae pelit</div>
            <v-btn
              color="primary"
              :loading="loading"
              @click="fetchGames"
              large
              block
            >
              <v-icon class="mr-2">mdi-download</v-icon>
              Hae pelit
            </v-btn>

            <div class="mt-4">
              <div class="text-body-2">
                <strong>Valittu päivä:</strong> {{ selectedDate }}
              </div>
              <div class="text-caption text-grey mt-1">
                Saattaa kestää aika kauan
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-sheet>

    <v-sheet v-if="loading" class="d-flex justify-center pa-8">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-sheet>

    <v-sheet v-else-if="games.length > 0">
      <v-card outlined class="pa-4 mb-4">
        <div class="text-h6">Löydetty {{ games.length }} peli(ä) {{ selectedDate }}</div>
        <div v-if="gamesOnDate.length > 0" class="text-body-2 text-grey mt-1">
          {{ gamesOnDate.length }} peli(ä) jo tallennettu tältä päivältä
        </div>
      </v-card>

      <v-row>
        <v-col cols="12">
          <GameCard
            v-for="game in games"
            :key="game.id + 'game'"
            :game="game"
            :importing="true"
            :saving="savingGames[game.id] || false"
            :already-saved="isGameAlreadySaved(game)"
            @save="handleSaveGame"
          ></GameCard>
        </v-col>
      </v-row>
    </v-sheet>

    <v-sheet v-else-if="hasSearched" class="text-center pa-8">
      <v-card outlined class="pa-8">
        <v-icon size="64" color="grey">mdi-calendar-blank</v-icon>
        <div class="text-h6 mt-4">Ei löytynyt peliä {{ selectedDate }}</div>
        <div class="text-body-2 text-grey">Yritä valita eri päivä</div>
      </v-card>
    </v-sheet>

    <v-sheet v-else class="text-center pa-8">
      <v-card outlined class="pa-8">
        <v-icon size="64" color="grey">mdi-calendar-search</v-icon>
        <div class="text-h6 mt-4">Valitse päivä ja hae pelit</div>
        <div class="text-body-2 text-grey">Napsauta "Hae pelit" painiketta saadaksesi listan saatavilla olevista pelistä</div>
      </v-card>
    </v-sheet>
  </v-container>
</template>

<script>
import GameCard from '@/components/tiimi_admin/league/game/GameCard.vue'
import a from 'axios'
import { mapActions } from 'vuex'
import { toCloudflareHlsUrl } from '@/utils/video/hlsUrls'

export default {
  name: 'AutoImport',
  components: {
    GameCard
  },
  data: () => ({
    selectedDate: null,
    loading: false,
    games: [],
    gamesOnDate: [], // Games already saved for this date
    hasSearched: false,
    savingGames: {} // Track saving state for each game
  }),
  computed: {
    today() {
      return new Date().toISOString().substr(0, 10)
    }
  },
  created() {
    // Set default date to today
    this.selectedDate = this.today
  },
  methods: {
    ...mapActions('admin', [
      'saveGame'
    ]),
    ...mapActions('noti', [
      'success',
      'error'
    ]),
    handleDateChange() {
      // Reset games when date changes
      this.games = []
      this.hasSearched = false
    },
    async fetchGames() {
      if (!this.selectedDate) {
        this.error('Please select a date')
        return
      }

      this.loading = true
      this.hasSearched = true

      try {
        const response = await a.get(`https://autoimport-games-api-ae87c61dfd3e.herokuapp.com/api/games?date=${this.selectedDate}`)
        this.games = (response.data?.data || []).map(game => ({
          ...game,
          video_url: toCloudflareHlsUrl(game.video_url),
          video_live_url: toCloudflareHlsUrl(game.video_live_url),
          second_video_url: toCloudflareHlsUrl(game.second_video_url),
          second_video_live_url: toCloudflareHlsUrl(game.second_video_live_url)
        }))
        this.gamesOnDate = response.data?.gamesOnDate || []
        this.success(`Found ${this.games.length} game(s)`)
      } catch (error) {
        console.error('Error fetching games:', error)
        this.error('Failed to fetch games from API')
        this.games = []
        this.gamesOnDate = []
      } finally {
        this.loading = false
      }
    },
    isGameAlreadySaved(game) {
      console.log('Checking if game is already saved:', {
        game_id: game.id,
        game_home: game.home_club_id,
        game_away: game.away_club_id,
        gamesOnDate: this.gamesOnDate
      })

      const isSaved = this.gamesOnDate.some(savedGame =>
        savedGame.home_club_id === game.home_club_id &&
        savedGame.away_club_id === game.away_club_id
      )

      console.log('Result:', isSaved)
      return isSaved
    },
    async handleSaveGame(game) {
      console.log('Saving game:', game.id, game.home_club_name, 'vs', game.away_club_name)
      console.log('Current savingGames state:', this.savingGames)

      // Set saving state for this specific game
      this.$set(this.savingGames, game.id, true)

      console.log('After setting saving state:', this.savingGames)

      try {
        // Prepare game data for saving
        const gameData = {
          ...game,
          league_id: game.league_id, // Default league ID if not in route
          // Ensure required fields are present
          home_team_id: game.home_team_id || null,
          away_team_id: game.away_team_id || null,
          shown_live: game.shown_live !== undefined ? game.shown_live : true,
          game_info: game.game_info || '',
          game_error: game.game_error || '',
          score_home: game.score_home || 0,
          score_away: game.score_away || 0,
          will_be_analyzed: game.will_be_analyzed !== undefined ? game.will_be_analyzed : true,
          starttime_unix: game.starttime_unix || null,
          publishtime_unix: game.publishtime_unix || null,
          video_url: toCloudflareHlsUrl(game.video_url) || '',
          video_live_url: toCloudflareHlsUrl(game.video_live_url) || '',
          video_type: game.video_type || 'hls',
          external_service_id: game.external_service_id || '',
          season_id: game.season_id || null,
          done_analysis_state: game.done_analysis_state || null
        }

        await this.saveGame(gameData)

        // Show success message
        this.success(`Game "${game.home_club_name} vs ${game.away_club_name}" saved successfully`)

        // Add the game to gamesOnDate so it's marked as already saved
        this.gamesOnDate.push({
          home_club_id: game.home_club_id,
          away_club_id: game.away_club_id
        })

        // Don't remove the game from the list since it doesn't have an ID yet
        // The game will be marked as "already saved" by the isGameAlreadySaved method

      } catch (error) {
        console.error('Error saving game:', error)
        this.error(`Failed to save game: ${error.message || 'Unknown error'}`)
      } finally {
        // Clear saving state for this game
        this.$set(this.savingGames, game.id, false)
        console.log('After clearing saving state:', this.savingGames)
      }
    }
  }
}
</script>